import React from 'react';
import { CountryTemplate } from '../../components/CountryTemplate';

export const MexicoPage = () => {
  const mexicoData = {
    name: 'Mexico',
    region: 'Latin America',
    color: '#708080',
    metaTitle: 'Pharma & Nutraceutical Registration in Mexico | COFEPRIS | CTD Dossiers & Supplements',
    metaDescription: 'Understand how to register pharmaceutical and nutraceutical products in Mexico. COFEPRIS requirements, CTD dossier structure, food-supplement rules, key documents and why Mexico is a strategic LatAm market for chronic and wellness portfolios.',
    
    hero: {
      title: 'Pharma & Nutraceutical Registration in Mexico',
      subtitle: 'One of Latin America\'s largest pharmaceutical markets with high chronic-disease burden and CTD-based registration through COFEPRIS - ideal for serious exporters ready to invest in quality dossiers.',
      cta: 'Start Your Mexico Registration'
    },
    
    atAGlance: [
      { label: 'Population', value: '~129 million' },
      { label: 'Income Level', value: 'Upper-middle income' },
      { label: 'Overweight & Obesity (Adults 15+)', value: '73.4% (2022)' },
      { label: 'Diabetes Drugs Market', value: 'USD 2.02B (2023) → USD 2.46B (2032)' },
      { label: 'Generic Drug Market', value: 'USD 7.4B (2024) → USD 12.1B (2033)' },
      { label: 'Registration Timeline', value: '180 days official (12-18 months practical)' }
    ],
    
    marketOverview: {
      title: 'Market Overview: Big, Chronic, and Generics-Driven',
      stats: [
        'One of LatAm\'s largest pharma markets with robust generic-drug segment at USD 7.4 billion (2024)',
        'Diabetes drugs market valued around USD 2.0 billion (2023) with strong growth projected',
        'Very high rates of overweight/obesity (73.4% of adults in 2022) confirming huge chronic-disease market',
        'Major multinationals prioritising Mexico for advanced diabetes/weight-loss drug launches',
        'All health products regulated by COFEPRIS',
        'COFEPRIS formally adopted CTD (Documento Técnico Común) as reference structure for registration dossiers'
      ],
      opportunities: [
        'Large population (129M) with high NCD burden creates sustained demand',
        'CTD-aligned regulator enables adaptation of quality dossiers from other markets',
        'Strong generics segment with growth trajectory',
        'Structured supplement control offers predictable pathways for compliant brands'
      ]
    },
    
    diseaseburden: {
      title: 'Disease Profile: Among the World\'s Highest Obesity Rates',
      subtitle: 'Mexico faces one of the world\'s highest overweight/obesity rates driving massive chronic disease burden.',
      categories: [
        {
          name: 'Non-Communicable Diseases',
          items: [
            '73.4% of adults (15+) are overweight or obese (2022) - among world\'s highest rates',
            'Substantial and growing burden of diabetes and diabetes-related chronic kidney disease',
            'Overweight/obesity central driver of diabetes, hypertension, coronary heart disease and stroke',
            'High prevalence of metabolic syndrome',
            'Pharma demand: Antidiabetics (including newer classes), insulin and diabetes-device combinations',
            'Pharma demand: Antihypertensives, lipid-lowering agents, CVD and renal drugs',
            'Long-term opportunity for biosimilars and specialised chronic therapies'
          ]
        },
        {
          name: 'Wellness & Prevention Market',
          items: [
            'Growing awareness of preventive health and lifestyle modification',
            'Large middle class with purchasing power for wellness products',
            'Demand for weight management and metabolic support supplements',
            'Nutra opportunities: Cardio health, omega-3, vitamin D and multivitamins',
            'Nutra opportunities: Specialised products for older adults and high-risk populations',
            'Must respect Mexico\'s strict supplement rules regarding claims and composition',
            'Clear differentiation needed from medicinal products'
          ]
        }
      ]
    },
    
    regulatoryFramework: {
      authority: 'COFEPRIS - Comisión Federal para la Protección contra Riesgos Sanitarios',
      description: 'COFEPRIS is the federal authority for medicines, biologicals, medical devices, food supplements, foods, beverages and advertising. Key regulations include Reglamento de Insumos para la Salud (health supplies) and Reglamento de Control Sanitario de Productos y Servicios (foods, beverages, supplements). COFEPRIS published the Documento Técnico Común (CTD) as reference for structuring dossiers.',
      pharma: {
        timeline: 'Official resolution time typically 180 calendar days for allopathic medicines. Real-world timelines often 12-18 months for complex registrations. Registration validity commonly 5 years requiring renewal.',
        requirements: [
          'Mexican legal entity (lab, importer or representative) required as registration holder',
          'Procedures registered under specific COFEPRIS homoclaves (e.g., COFEPRIS-04-004 for allopathic drugs)',
          'CTD format mandatory - reference for registration of medicines and biologicals',
          'Module 1: Mexico-specific administrative info (legal docs, manufacturing authorizations, MAH, prescribing info)',
          'Module 2: Quality overall summary, non-clinical and clinical overviews',
          'Module 3: Quality/CMC details, specifications, stability (referencing NOM-073-SSA1-2015)',
          'Modules 4 & 5: Non-clinical and clinical data (depending on new molecule, biologic, or generic)',
          'GMP certificate from manufacturing site',
          'CPP (Certificate of Pharmaceutical Product) or equivalent from reference authority',
          'Draft labels, carton and insert in Spanish',
          'Proof of payment and required administrative forms'
        ],
        insight: 'COFEPRIS has strengthened digital CTD format requirements with detailed guidance on searchable PDFs and specific structure. While official timelines state 180 days, practical experience shows 12-18 months for complex products. Initiatives underway to reduce processing times. Registration valid 5 years with renewal process.'
      },
      nutra: {
        timeline: 'Faster than pharmaceutical route if correctly classified. PSPI (Permiso Sanitario Previo de Importación) required for imported products with review of label and ingredient compliance.',
        requirements: [
          'Suplementos alimenticios regulated under Reglamento de Control Sanitario de Productos y Servicios',
          'COFEPRIS Marco jurídico sets restrictions on composition, labelling, advertising and claims',
          'Cannot claim to cover all nutritional requirements or prevent/cure disease',
          'Imported supplements require PSPI - authority reviews label and ingredient list for compliance',
          'Classification depends on: composition (nutrient levels/types), dosage form/route, claims (nutritional vs therapeutic)',
          'NOM-051-SCFI/SSA1-2010 establishes labelling requirements for prepacked foods/beverages',
          'Some supplements exempt from NOM-051 when other specific standards apply',
          'Advertising needs separate COFEPRIS permission',
          'Must respect Acuerdo on additives and processing aids',
          'Strict enforcement of forbidden claims and legends'
        ],
        insight: 'Mexico has clear but strict supplement framework. Classification as suplemento vs medicine critical - wrong classification costly. PSPI process for imports includes detailed label and ingredient review. Advertising heavily regulated with separate approval needed. Success requires careful navigation of composition limits and claim restrictions.'
      }
    },
    
    pharmaPathway: {
      title: 'Pharmaceutical Registration with COFEPRIS (CTD-Based)',
      steps: [
        'Local Holder & Access - Mexican legal entity (lab, importer or representative) required as registration holder',
        'Registration under specific COFEPRIS homoclaves (e.g., COFEPRIS-04-004 for allopathic drugs)',
        'Dossier Format - CTD mandatory as reference for medicines and biologicals',
        'Digital CTD compilation - Searchable PDFs with specific structure per COFEPRIS guidance',
        'Module 1: Mexico regional/administrative info (legal docs, manufacturing authorizations, MAH, proposed prescribing info)',
        'Module 2: Quality overall summary, non-clinical and clinical overviews',
        'Module 3: Quality/CMC details, specifications, stability (NOM-073-SSA1-2015 for stability testing)',
        'Modules 4 & 5: Non-clinical and clinical data (depending on product type)',
        'GMP certificate from manufacturing site',
        'CPP or equivalent from reference authority',
        'Draft labels, carton and insert in Spanish',
        'Formal application form, cover letter, proof of payment',
        'Assessment: Official 180 days, practical 12-18 months for complex registrations',
        'Renewal: Typically 5-year validity, renewal initiated months before expiry',
        'Modifications: Site change, label changes, formulation tweaks under specific COFEPRIS modalities'
      ]
    },
    
    nutraPathway: {
      title: 'Nutraceuticals & Suplementos Alimenticios in Mexico',
      steps: [
        'Legal Framework: Regulated under Reglamento de Control Sanitario de Productos y Servicios',
        'COFEPRIS Marco jurídico summarises legal constraints on composition, labelling, advertising',
        'Acuerdo on additives sets which additives allowed and under what conditions',
        'Import Control: Imported supplements require PSPI (Permiso Sanitario Previo de Importación)',
        'PSPI process: COFEPRIS reviews label and ingredient list to confirm regulatory compliance',
        'Labelling: NOM-051-SCFI/SSA1-2010 establishes front-of-pack and general labelling requirements',
        'Some supplements exempt from NOM-051 when other specific standards apply',
        'Classification determination critical: composition (nutrient levels and types), dosage form/route, claims',
        'PSPI application with full ingredient list and formulation',
        'COA for finished product; sometimes raw-material COAs',
        'Label artwork in Spanish with mandatory information and forbidden claims legends',
        'Evidence composition respects Reglamento and additive agreement',
        'Free Sale Certificate / manufacturing authorizations where relevant',
        'Advertising approval (separate process) if marketing campaigns planned',
        'Avoid claims that induce harmful eating habits or suggest product treats diseases'
      ]
    },
    
    requiredDocs: {
      'Pharmaceutical Products (Medicamentos)': [
        'Application form (COFEPRIS standard format)',
        'CTD dossier in digital form (searchable PDFs)',
        'Module 1: Administrative/region-specific info',
        'Module 2: Overviews/summaries',
        'Module 3: Quality/CMC, stability data',
        'Modules 4 & 5: Non-clinical and clinical (as applicable)',
        'GMP certificate of manufacturing site',
        'CPP or equivalent regulatory certificate',
        'Draft artwork and inserts in Spanish',
        'Fee payment proof and required forms'
      ],
      'Food Supplements (Suplementos Alimenticios)': [
        'PSPI application for imported products',
        'Full ingredient list and formulation (active and excipients)',
        'COA for finished product; raw-material COAs if needed',
        'Label artwork in Spanish with mandatory information',
        'Evidence composition respects Reglamento and additive agreement',
        'Free Sale Certificate / manufacturing authorizations',
        'Advertising approval (separate) if campaigns planned'
      ]
    },
    
    whyThisCountry: {
      title: 'Why Mexico Should Anchor Your LatAm Strategy',
      reasons: [
        'Large chronic-heavy market: 129 million people, ~73% overweight/obese, high diabetes burden',
        'Strong generics segment: USD 7.4 billion market growing to USD 12.1 billion by 2033',
        'Diabetes drugs market: USD 2 billion with steady growth forecasts',
        'CTD-aligned regulator: COFEPRIS uses CTD framework enabling quality dossier adaptation',
        'Structured supplement control: Clear legal framework offers predictable pathways',
        'Strategic LatAm gateway: Success in Mexico facilitates broader regional expansion',
        'Growing middle class with purchasing power for wellness products'
      ],
      bestPortfolio: [
        'Pharma: Antidiabetics (oral + injectables), cardiovascular, kidney, obesity-related therapies, essential generics for public procurement, biosimilars and specialised chronic therapies',
        'Nutra: Weight-management and metabolic-support (avoiding medicinal claims), heart-health, omega-3, joint-health, multivitamin formulations tailored to Mexican NCD risk patterns'
      ]
    },
    
    howWeHelp: {
      title: 'How TheApostrophe Helps You Enter Mexico',
      services: [
        'Portfolio & Disease-Burden Mapping - Identify products matching Mexico\'s obesity/diabetes profile and competitive positioning',
        'CTD Dossier Development for COFEPRIS - Restructure into Mexico-ready digital CTD following latest guidance and NOM-073',
        'Supplement Classification & Compliance - Decide suplementos vs medicines path, align composition, claims and labelling',
        'PSPI & Import-Chain Setup - Guide Mexican partner through PSPI applications, label verification, ingredient checks',
        'Spanish Documentation - Prepare all labels, inserts, and documentation in compliant Spanish',
        'Lifecycle & LatAm Cluster Planning - Use Mexico CTD as base asset for other LatAm markets',
        'Advertising Strategy - Navigate separate COFEPRIS advertising approval requirements'
      ]
    },
    
    faqs: [
      {
        question: 'Who regulates medicines and supplements in Mexico?',
        answer: 'COFEPRIS (Comisión Federal para la Protección contra Riesgos Sanitarios) regulates medicines, biologicals, medical devices, foods, beverages, supplements and related advertising.'
      },
      {
        question: 'Does Mexico use CTD?',
        answer: 'Yes. COFEPRIS has adopted the Documento Técnico Común (CTD) as the standard structure for medicine and biological registration dossiers and has published detailed CTD guides and digital instructions.'
      },
      {
        question: 'How long does drug registration take?',
        answer: 'Official time tables indicate around 180 days for many allopathic drug registrations, but industry data show times can extend to 12-18 months depending on complexity and backlog. Reforms aim to shorten this.'
      },
      {
        question: 'How are food supplements regulated?',
        answer: 'Supplements are governed by the Reglamento de Control Sanitario de Productos y Servicios. Imported supplements need PSPI and must comply with ingredient and labelling rules, plus strict limits on advertising claims.'
      },
      {
        question: 'Does NOM-051 apply to supplements?',
        answer: 'NOM-051 applies broadly to prepackaged foods and beverages. Some supplements are exempt when covered by other specific standards, but many labelling principles (Spanish language, truthful statements, warning legends) still influence supplementary products.'
      }
    ]
  };

  return <CountryTemplate countryData={mexicoData} />;
};
